/**
 * InvitePreview — Public magic-link landing page for org invitations.
 * Route: /invite/:token
 *
 * Handles Google-account-conflict safety:
 *   - Unauthed → shows a pre-filled email hint and a big "Continue with Google" CTA.
 *   - Authed with matching email → shows "You're in!" and redirects to /dashboard.
 *   - Authed with a DIFFERENT email → shows a hard warning explaining they must
 *     sign out and re-authenticate with the invited address to avoid mixing orgs.
 *   - Expired / revoked / accepted invites → distinct sad-path panels.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ShieldCheck, ArrowRight, AlertTriangle, MailCheck, Clock, XCircle, Users } from "lucide-react";

export default function InvitePreview() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    api.get(`/org/invite/preview?token=${encodeURIComponent(token || "")}`)
      .then((r) => alive && setState({ loading: false, data: r.data, error: null }))
      .catch((e) => {
        if (!alive) return;
        const s = e?.response?.status;
        const d = e?.response?.data?.detail;
        setState({
          loading: false,
          data: null,
          error: {
            status: s || 500,
            kind: s === 410 ? (d?.status || "expired") : "not_found",
            target_email: d?.target_email || null,
          },
        });
      });
    return () => { alive = false; };
  }, [token]);

  const inv = state.data;
  const emailMatches = user && inv && (user.email || "").toLowerCase() === (inv.target_email || "").toLowerCase();

  // Auto-redirect authed + matching users to dashboard (auto-consume already ran at sign-in)
  useEffect(() => {
    if (!authLoading && emailMatches) {
      const t = setTimeout(() => navigate("/dashboard", { replace: true }), 1400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [authLoading, emailMatches, navigate]);

  const startGoogleSignIn = () => {
    // Existing OAuth pattern used by Login.jsx — do NOT modify.
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid-bg flex flex-col" data-testid="invite-preview-page">
      <header className="px-6 sm:px-8 py-6 flex items-center justify-between">
        <Logo size="md" />
        <button
          onClick={() => navigate("/")}
          data-testid="invite-back-btn"
          className="text-slate-400 hover:text-white text-sm"
        >
          ← Back
        </button>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs font-semibold uppercase tracking-widest mb-5">
            <Users size={12} /> Team Invite
          </div>

          {/* Loading */}
          {state.loading && (
            <div
              data-testid="invite-loading"
              className="glass rounded-3xl p-10 text-center text-slate-400 font-mono text-sm tracking-widest"
            >
              LOADING INVITE…
            </div>
          )}

          {/* Error: 404 not found */}
          {!state.loading && state.error?.kind === "not_found" && (
            <div className="glass rounded-3xl p-10 text-center" data-testid="invite-error-notfound">
              <XCircle size={40} className="text-red-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white tracking-tight">Invite not found</h1>
              <p className="text-slate-400 mt-3 text-sm">
                This magic link doesn&apos;t match any active invite. Ask your team lead to send a fresh invitation.
              </p>
              <button
                onClick={() => navigate("/")}
                data-testid="invite-goto-home"
                className="mt-6 h-11 px-5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm"
              >
                Go home
              </button>
            </div>
          )}

          {/* Error: 410 gone (expired / revoked / accepted) */}
          {!state.loading && state.error && state.error.status === 410 && (
            <div className="glass rounded-3xl p-10 text-center" data-testid="invite-error-gone">
              {state.error.kind === "accepted" ? (
                <MailCheck size={40} className="text-emerald-400 mx-auto mb-4" />
              ) : (
                <Clock size={40} className="text-amber-400 mx-auto mb-4" />
              )}
              <h1 className="text-2xl font-bold text-white tracking-tight">
                {state.error.kind === "accepted" && "Already accepted"}
                {state.error.kind === "revoked" && "Invite revoked"}
                {state.error.kind === "expired" && "Invite expired"}
              </h1>
              <p className="text-slate-400 mt-3 text-sm">
                {state.error.kind === "accepted" && "This invite has already been claimed. Sign in to continue."}
                {state.error.kind === "revoked" && "Your team lead revoked this invite. Ask them to send a new one."}
                {state.error.kind === "expired" && "Magic links are good for 14 days. Ask your team lead for a fresh invite."}
              </p>
              <button
                onClick={() => navigate("/login")}
                data-testid="invite-goto-login"
                className="mt-6 h-11 px-5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm"
              >
                Sign in
              </button>
            </div>
          )}

          {/* Success: live invite */}
          {!state.loading && inv && (
            <div className="glass rounded-3xl p-8 sm:p-10 fade-up" data-testid="invite-card">
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter text-white leading-tight">
                <span data-testid="invite-inviter-name">{inv.inviter_name}</span>
                <span className="text-slate-400"> invited you to their</span>{" "}
                <span className="text-emerald-400">Receipt Sheets</span> org.
              </h1>

              <div className="mt-5 rounded-xl bg-white/[0.03] border border-emerald-500/25 p-4 flex items-center gap-3" data-testid="invite-inviter-block">
                <div className="w-11 h-11 rounded-full bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center text-emerald-300 font-semibold shrink-0 overflow-hidden">
                  {inv.inviter_picture
                    ? <img src={inv.inviter_picture} alt="" className="w-full h-full object-cover" />
                    : (inv.inviter_name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-white text-sm font-medium truncate">{inv.inviter_name}</div>
                  <div className="text-[11px] text-slate-500 font-mono truncate">{inv.inviter_email}</div>
                </div>
                <div className="ml-auto text-right shrink-0">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">Org</div>
                  <div className="text-white font-mono text-xs">····{inv.org_id_short}</div>
                </div>
              </div>

              {/* Pre-filled email hint (multi-account safety wrap) */}
              <div className="mt-4 p-4 rounded-xl border border-white/10 bg-white/[0.02]">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1.5">Sign in with this address</div>
                <div className="flex items-center gap-2">
                  <MailCheck size={16} className="text-emerald-400 shrink-0" />
                  <div
                    data-testid="invite-target-email"
                    className="text-white font-mono text-sm truncate flex-1"
                  >
                    {inv.target_email}
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                  Google may auto-pick a different account. On the picker, choose <span className="text-emerald-300 font-mono">{inv.target_email}</span> to auto-adopt your seat.
                </div>
              </div>

              {/* --- Authed states --- */}
              {!authLoading && user && emailMatches && (
                <div className="mt-6 p-4 rounded-xl border border-emerald-500/40 bg-emerald-500/[0.08]" data-testid="invite-already-in">
                  <div className="flex items-start gap-3">
                    <ShieldCheck size={20} className="text-emerald-300 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-white font-semibold">You&apos;re in!</div>
                      <div className="text-emerald-200/80 text-sm mt-1">
                        Signed in as {user.email}. Redirecting to your dashboard…
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!authLoading && user && !emailMatches && (
                <div className="mt-6 p-4 rounded-xl border border-amber-500/40 bg-amber-500/[0.08]" data-testid="invite-wrong-account">
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="text-amber-300 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="text-white font-semibold">Wrong Google account signed in</div>
                      <div className="text-amber-200/80 text-sm mt-1 leading-relaxed">
                        You&apos;re currently signed in as{" "}
                        <span className="font-mono text-white" data-testid="invite-current-email">{user.email}</span>.
                        This invite is for{" "}
                        <span className="font-mono text-emerald-300">{inv.target_email}</span>.
                        Sign out and use the invited address to auto-adopt your seat.
                      </div>
                      <div className="mt-3 flex gap-2 flex-wrap">
                        <button
                          onClick={async () => {
                            try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
                            window.location.reload();
                          }}
                          data-testid="invite-signout-btn"
                          className="h-9 px-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-xs"
                        >
                          Sign out & retry
                        </button>
                        <button
                          onClick={() => navigate("/dashboard")}
                          data-testid="invite-continue-as-current"
                          className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-slate-200 font-medium text-xs border border-white/10"
                        >
                          Skip — continue as {user.email?.split("@")[0]}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!authLoading && !user && (
                <button
                  onClick={startGoogleSignIn}
                  data-testid="invite-google-btn"
                  className="mt-6 w-full h-14 rounded-xl bg-white text-slate-900 font-semibold text-base flex items-center justify-center gap-3 hover:bg-slate-100 transition-colors"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continue with Google <ArrowRight size={16} />
                </button>
              )}

              <div className="mt-5 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-slate-500">
                <span data-testid="invite-expires">Expires in {inv.days_remaining}d</span>
                <span>Receipt Sheets · Team Invite</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
